import {
    PropertiesPanelContent,
    PropertiesPanelHint,
    PropertiesPanelPropertyConfig
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { stringFormat, T } from 'src/shared/helpers';
import { createTripleNumberConfig } from './propertyConfigs';

export enum CameraProperty {
    CAMERA_POSITION_FOV,
    CAMERA_HEIGHT,
    CAMERA_LIMIT_ZOOM,
    CAMERA_ENABLE_LIMIT_MIN_ZOOM,
    CAMERA_ENABLE_LIMIT_MAX_ZOOM,
    CAMERA_LIMIT_MIN_ZOOM,
    CAMERA_LIMIT_MAX_ZOOM,
    CAMERA_LOCK_HORIZONTAL,
    CAMERA_LOCK_VERTICAL,
    CAMERA_MIN_ALPHA,
    CAMERA_MAX_ALPHA,
    CAMERA_MIN_BETA,
    CAMERA_MAX_BETA,

    /** Non è un valore che cambia: è la richiesta di rimetterli tutti come erano all'inizio. */
    CAMERA_RESET,

    /** La camera del negozio gira da sola intorno al prodotto. */
    CAMERA_AUTO_ROTATE
}

/**
 * COM'ERANO ALL'INIZIO, dichiarato una volta sola. Non sono numeri scelti adesso: sono gli stessi
 * su cui il pannello ricade quando la scena non ha mai avuto un valore suo, quindi «Reset» rimette
 * davvero le cose come se nessuno le avesse toccate — invece di inventare un secondo inizio.
 *
 * `fov` in radianti e gli zoom in frazioni, come li vuole il motore: la conversione in gradi e in
 * centesimi la fa chi li mostra.
 */
export const CAMERA_DEFAULTS = {
    fov: 1,
    // A METÀ DEL MODELLO: è il valore con cui il motore nasce (`_positionScale = 0.5`). Lo zero di
    // prima faceva dire al pannello «base» mentre la camera girava a metà, e «Reset» la mandava davvero
    // alla base, anche in vetrina.
    height: 0.5,
    minZoom: 0,
    maxZoom: 1
};

/**
 * LA SCHEDA DI UNA CAMERA SALVATA mostra ciò che una camera salvata HA: dov'è e cosa guarda. Il
 * motore ne conserva sette campi — identificativo, nome, `px py pz`, `tx ty tz` — e non uno di più.
 *
 * QUELLO CHE MANCA NON È UNA DIMENTICANZA. Campo visivo, zoom, limiti e rotazione automatica il
 * mockup li disegna qui, dentro ogni camera; il motore li tiene sulla SCENA. Metterli qui
 * prometterebbe che sono di questa camera: otto camere mostrerebbero otto copie dello stesso numero,
 * e toccarne una le cambierebbe tutte. Stanno dove sono davvero, in «Free camera».
 *
 * E NON C'È LA ROTAZIONE. Una camera salvata è definita da dov'è e da cosa guarda: come è girata è la
 * conseguenza, non un dato. I primi due assi si potrebbero calcolare muovendo il bersaglio, il terzo —
 * il rollio — non ha dove esistere.
 */
export type CameraLocationNode = {
    position: [number, number, number];
    target: [number, number, number];
};

/**
 * L'IMMAGINE DEL CARRELLO (prototipo D165, Settings › Output). Il customizer riconosce le camere del riepilogo
 * ordine dal NOME: `buy_screenshot_camera` è l'immagine principale, `buy_screenshot_camera_1`, `_2`… quelle in
 * più. Non c'è un campo apposta, quindi il flag rinomina la camera (utente, 2026-10-19: «accettando la rinomina»).
 */
export type CameraCartOutput = {
    /** Il numero di questa camera nel carrello; `null` se non ne fa parte. */
    number: number | null;
    /** I numeri fra cui scegliere, e quelli già presi da un'altra camera. */
    choices: { value: number; taken: boolean }[];
    setEnabled: (enabled: boolean) => void;
    setNumber: (number: number) => void;
};

export type CameraLocationHelpers = {
    onChange: (what: 'position' | 'target', axis: 'x' | 'y' | 'z', value: number) => void;
    output?: CameraCartOutput;
};

const createCameraOutputGroup = (output: CameraCartOutput): PropertiesPanelPropertyConfig => {
    const inCart = output.number !== null;

    return createPropertyConfig({
        type: 'group',
        id: 'camera-output',
        header: T._('Output', 'Admin'),
        properties: [
            createPropertyConfig({
                type: 'switch',
                id: 'camera-cart-preview',
                label: T._('Cart preview image', 'Admin'),
                caption: T._('Used in the order summary', 'Admin'),
                checked: inCart,
                onChange: (checked) => output.setEnabled(checked)
            }),
            // SEMPRE VISIBILE, SPENTO FINCHÉ IL FLAG È SPENTO (prototipo): un numero per camera, e sceglierne uno
            // già preso scambia le due camere.
            createPropertyConfig({
                type: 'select',
                id: 'camera-cart-number',
                label: T._('Number', 'Admin'),
                disabled: !inCart,
                value: inCart ? { value: String(output.number), label: String(output.number) } : null,
                options: output.choices.map((choice) => ({
                    value: String(choice.value),
                    label:
                        choice.value === 0
                            ? T._('0 · main image', 'Admin')
                            : choice.taken
                              ? stringFormat(T._('{0} · taken', 'Admin'), String(choice.value))
                              : String(choice.value)
                })),
                onChange: (option: any) => {
                    const chosen = Number(option?.value);
                    if (Number.isFinite(chosen)) output.setNumber(chosen);
                },
                // LA RIGA CHE DICE CHE COSA È IL NUMERO SCELTO (prototipo; Appendice H): spenta, come funziona la serie.
                caption: !inCart
                    ? T._('0 is the main image of the cart preview, the others follow it.', 'Admin')
                    : output.number === 0
                      ? T._('The main image of the cart preview.', 'Admin')
                      : T._('An extra image in the cart preview, after the main one.', 'Admin')
            })
        ]
    });
};

export const useCameraLocationPropertiesCreator =
    (): ((camera: CameraLocationNode, helpers: CameraLocationHelpers) => PropertiesPanelContent[]) =>
    (camera: CameraLocationNode, helpers: CameraLocationHelpers) => [
        // UNA CAMERA SALVATA NON HA TAB (utente, 2026-10-20): dove sta e se va nel carrello sono pochi campi, e
        // dividerli in General e Settings era un clic in più per niente. Un capitolo solo non mostra né tab né etichetta.
        createPropertyConfig({
            type: 'section',
            id: 'camera-location',
            title: T._('General', 'Admin'),
            properties: [
                createPropertyConfig({
                    type: 'group',
                    id: 'transform',
                    header: T._('Transform', 'Admin'),
                    flat: true,
                    defaultIsOpen: true,
                    properties: [
                        createTripleNumberConfig('position', T._('Position', 'Admin'), camera.position, (axis, value) =>
                            helpers.onChange('position', axis, value)
                        ),

                        // IL BERSAGLIO È IL PUNTO CHE LA CAMERA GUARDA, e sta sotto la posizione perché si
                        // legge dopo: prima dov'è, poi verso dove. Il mockup lo chiama «Target» e gli dà tre
                        // modi — spento, legato a una mesh, a mano. Qui può essere solo a mano: gli altri due
                        // vorrebbero un campo che la camera salvata non ha.
                        createTripleNumberConfig('target', T._('Target', 'Admin'), camera.target, (axis, value) => helpers.onChange('target', axis, value))
                    ]
                }),
                ...(helpers.output ? [createCameraOutputGroup(helpers.output)] : [])
            ]
        })
    ];

export type CameraConfigHelpers = {
    onPropertyChange: (property: CameraProperty, value?: any) => void;
};

export type CameraNode = {
    id: string;
    name: string;
    position: [number, number, number];
    target: [number, number, number];
    fov: number;
    isActive: boolean;
    enableZoomLimit: boolean;
    // Proprietà zoom
    useMinZoomPerc: boolean;
    useMaxZoomPerc: boolean;
    minZoomPerc: number;
    maxZoomPerc: number;
    // Proprietà rotazione
    lowerBetaLimits: number | undefined;
    upperBetaLimits: number | undefined;
    lowerAlphaLimit: number | undefined;
    upperAlphaLimit: number | undefined;
    height: number;
    autoRotate?: boolean;
};

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

/**
 * IL PUNTO ATTORNO A CUI GIRA LA FREE CAMERA (QA, 2026-10-14). Si chiamava «Height», e sembrava
 * l'altezza della camera: è invece quella del suo bersaglio, in frazione dell'altezza del modello —
 * 0 la base, 1 la cima. Lo si mostra come le altre camere, una terna sotto «Transform», così si legge
 * che qui si regola una camera e non un'impostazione.
 *
 * IN PERCENTUALE SU TUTT'E TRE GLI ASSI, come il vecchio backoffice: X e Z sono il centro del modello,
 * cioè il 50%, e non si scelgono — il motore non ha dove tenerli. Spenti, ma veri: uno zero in unità
 * del mondo sarebbe stato un numero inventato.
 */
const createFreeCameraTransformGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig =>
    createPropertyConfig({
        type: 'group',
        id: 'free-camera-transform',
        header: T._('Transform', 'Admin'),
        flat: true,
        hint: (
            <PropertiesPanelHint
                text={T._('The point the camera orbits around, as a percentage of the model size. X and Z stay at the center of the model.', 'Admin')}
            />
        ),
        defaultIsOpen: true,
        properties: [
            createPropertyConfig({
                type: 'point',
                id: 'target-position',
                label: T._('Target position', 'Admin'),
                coords: [
                    { id: 'x', label: 'X', value: 50, unit: '%', disabled: true },
                    {
                        id: 'y',
                        label: 'Y',
                        value: Math.round(camera.height * 100),
                        min: 0,
                        max: 100,
                        step: 1,
                        unit: '%',
                        onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_HEIGHT, value / 100)
                    },
                    { id: 'z', label: 'Z', value: 50, unit: '%', disabled: true }
                ]
            })
        ]
    });

// Configurazione per il gruppo delle proprietà di base della camera
const createBasicCameraPropertiesGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'camera-properties',
        header: T._('Camera', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // IL CAMPO VISIVO SI LEGGE IN GRADI, e si scrive in radianti. Il motore tiene il valore di
            // Babylon, che è in radianti: mostrarlo così com'è dava una scala da 0 a 2 che non vuol dire
            // niente per chi la guarda — un obiettivo si sceglie in gradi. La conversione sta QUI, al
            // confine fra ciò che si legge e ciò che si salva, e non nel motore né nello stato.
            //
            // Da 10 a 120 gradi: è la scala del mockup, ed è quella entro cui una inquadratura ha senso.
            createPropertyConfig({
                type: 'range',
                id: 'fov',
                label: T._('FOV', 'Admin'),
                value: Math.round((camera.fov * 180) / Math.PI),
                min: 10,
                max: 120,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_POSITION_FOV, (value * Math.PI) / 180)
            })
        ]
    });
};

// Configurazione per il gruppo dei limiti di zoom
const createZoomLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const properties: PropertiesPanelPropertyConfig[] = [];

    // Se zoom limit è abilitato, aggiungi i controlli
    if (camera.enableZoomLimit) {
        // Min zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-min-zoom',
                label: T._('Enable the minimum zoom limit', 'Admin'),
                checked: camera.useMinZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MIN_ZOOM, checked)
            })
        );

        if (camera.useMinZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'min-zoom-perc',
                    label: T._('Min', 'Admin'),
                    value: camera.minZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MIN_ZOOM, value / 100)
                })
            );
        }

        // Max zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-max-zoom',
                label: T._('Enable the maximum zoom limit', 'Admin'),
                checked: camera.useMaxZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MAX_ZOOM, checked)
            })
        );

        if (camera.useMaxZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'max-zoom-perc',
                    label: T._('Max', 'Admin'),
                    value: camera.maxZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MAX_ZOOM, value / 100)
                })
            );
        }
    }

    // L'INTERRUTTORE IN TESTATA (prototipo D130): spenta, resta l'etichetta; accesa, i due limiti.
    return createPropertyConfig({
        type: 'group',
        id: 'zoom-limits',
        header: T._('Zoom limits', 'Admin'),
        trailing: createPropertyConfig({
            type: 'switch',
            id: 'enable-zoom-limit',
            label: T._('Zoom limits', 'Admin'),
            checked: camera.enableZoomLimit,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_ZOOM, checked)
        }),
        properties
    });
};

// Configurazione per il gruppo dei limiti di rotazione
const createRotationLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const isAlphaLimited = camera.upperAlphaLimit != null && camera.lowerAlphaLimit != null;
    const isBetaLimited = camera.upperBetaLimits != null && camera.lowerBetaLimits != null;

    const properties: PropertiesPanelPropertyConfig[] = [
        // Horizontal rotation limit
        createPropertyConfig({
            type: 'switch',
            id: 'horizontal-rotation-limit',
            label: T._('Horizontal rotation limit', 'Admin'),
            checked: isAlphaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_HORIZONTAL, checked)
        })
    ];

    if (isAlphaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-alpha',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_ALPHA, value * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-alpha',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_ALPHA, value * (Math.PI / 180))
            })
        );
    }

    // Vertical rotation limit
    properties.push(
        createPropertyConfig({
            type: 'switch',
            id: 'vertical-rotation-limit',
            label: T._('Vertical rotation limit', 'Admin'),
            checked: isBetaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_VERTICAL, checked)
        })
    );

    if (isBetaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-beta',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_BETA, (value + 90) * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-beta',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_BETA, (value + 90) * (Math.PI / 180))
            })
        );
    }

    return createPropertyConfig({
        type: 'group',
        id: 'rotation-limits',
        header: T._('Rotation limits', 'Admin'),
        defaultIsOpen: true,
        properties
    });
};

// LA COLONNA NON HA TAB (utente, 2026-10-20): General e Settings dividevano pochi campi in due clic. Resta
// l'ordine del mockup, in una colonna sola: sopra la camera come è fatta, sotto le regole che le si danno —
// i limiti sono impostazioni, non ottica.
const cameraPropertiesByType = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelContent[] => {
    return [
        createPropertyConfig({
            type: 'section',
            id: 'camera-general',
            title: T._('General', 'Admin'),
            properties: [
                createFreeCameraTransformGroup(camera, helpers),
                createBasicCameraPropertiesGroup(camera, helpers),
                createZoomLimitsGroup(camera, helpers),
                createRotationLimitsGroup(camera, helpers),

                // AUTO ROTATE STA CON LA CAMERA (prototipo, Settings › Behavior; utente, 2026-10-19). Il motore lo
                // tiene sulla camera della scena, che è questa: non c'è una rotazione per ogni camera salvata.
                createPropertyConfig({
                    type: 'group',
                    id: 'camera-behavior',
                    header: T._('Behavior', 'Admin'),
                    properties: [
                        createPropertyConfig({
                            type: 'switch',
                            id: 'camera-auto-rotate',
                            label: T._('Auto rotate', 'Admin'),
                            caption: T._('In the storefront the camera turns around the product until the buyer touches it', 'Admin'),
                            checked: Boolean(camera.autoRotate),
                            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_AUTO_ROTATE, checked)
                        })
                    ]
                }),

                // FUORI DAI DUE GRUPPI, e in fondo. Dentro uno dei due sembrerebbe azzerare quello e
                // basta; qui attraversa la colonna e si legge dopo aver visto tutto ciò che rimette a
                // posto. Il nome dice il perimetro, perché un «Reset» da solo non lo direbbe.
                createPropertyConfig({
                    type: 'action',
                    id: 'camera-reset',
                    label: T._('Reset all camera settings', 'Admin'),
                    onClick: () => helpers.onPropertyChange(CameraProperty.CAMERA_RESET)
                })
            ]
        })
    ];
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useCameraPropertiesCreator = (): ((camera: CameraNode, helpers: CameraConfigHelpers) => PropertiesPanelContent[]) => {
    return (camera: CameraNode, helpers: CameraConfigHelpers) => {
        return cameraPropertiesByType(camera, helpers);
    };
};
